import { createStyles, createTheme, makeStyles } from '@material-ui/core';

declare module '@material-ui/core/styles/createBreakpoints' {
    interface BreakpointOverrides {
        xs: true;
        sm: true;
        md: true;
        lg: true;
        xl: true;
        tablet: true;
    }
}

export interface ButtonWidths {
    small: string;
    medium: string;
    large: string;
    xlarge: string;
    full: string;
}

export interface ButtonHeights {
    large: string;
    regular: string;
    compact: string;
}

declare module '@material-ui/core/styles/createTheme' {
    interface Theme {
        buttonWidths: ButtonWidths;
        buttonHeights: ButtonHeights;
    }
    // allow configuration using `createMuiTheme`
    interface ThemeOptions {
        buttonWidths: ButtonWidths;
        buttonHeights: ButtonHeights;
    }
}

export const LatoWeights = {
    Regular: 400,
    Bold: 700,
};

export const useBody3Variant = makeStyles(
    () =>
        // TODO move these styles to theme.ts as custom 'body3' variant with Material UI v5
        createStyles({
            root: {
                '&.MuiTypography-body2': {
                    fontWeight: LatoWeights.Regular,
                    fontSize: '0.875rem',
                    lineHeight: '1.6em',
                },
            },
        }),
    { name: 'MuiOTMbody2' },
);

const theme = createTheme({
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 996,
            lg: 1280,
            xl: 1920,
            tablet: 768,
        },
    },
    palette: {
        type: 'light',
        background: {
            default: '#FFFFFF',
            paper: '#F7FBFE',
        },
        primary: { main: '#0CBBF0', contrastText: '#FFFFFF', light: '#F1F9FD' },
        secondary: { light: '#FBFBFB', main: '#93A9B5', contrastText: '#FFFFFF' },
        error: {
            main: '#FE2F7C',
            light: '#FFE9F3',
        },
        success: {
            main: '#70DCA8',
            light: '#ccfaed',
            dark: '#47515B',
        },
        text: {
            primary: '#4F505D',
            secondary: '#607691',
            disabled: '#94AAC3',
        },
        action: {
            disabledBackground: '#D6DDE6',
        },
    },
    typography: {
        fontFamily: ['Lato', 'sans-serif'].join(','),
        htmlFontSize: 16,
        h1: {
            fontFamily: 'Lato',
            fontWeight: LatoWeights.Bold,
            fontSize: '3.125rem',
            lineHeight: '1.11em',
        },
        h2: {
            fontWeight: LatoWeights.Bold,
            fontSize: 24,
            lineHeight: '1.3em',
        },
        h3: {
            fontWeight: LatoWeights.Bold,
            fontSize: 20,
            lineHeight: '1.4em',
        },
        h4: {
            fontWeight: LatoWeights.Bold,
            fontSize: 18,
            lineHeight: '1.4em',
        },
        h5: {
            fontWeight: LatoWeights.Bold,
            fontSize: 16,
            lineHeight: '1.4em',
        },
        h6: {
            fontWeight: LatoWeights.Bold,
            fontSize: 16,
            lineHeight: '1.4em',
        },
        subtitle1: {
            fontWeight: LatoWeights.Regular,
            fontSize: '1rem',
            lineHeight: '1.4em',
        },
        subtitle2: {
            fontWeight: LatoWeights.Regular,
            fontSize: '0.875rem',
            lineHeight: '1.4em',
        },
        body1: {
            fontFamily: 'Lato',
            fontWeight: LatoWeights.Regular,
            fontSize: 16,
            lineHeight: '1.7em',
            letterSpacing: '0.005em',
        },
        body2: {
            fontWeight: LatoWeights.Regular,
            fontSize: 16,
            lineHeight: '1.6em',
            letterSpacing: '0.0051em',
        },
        button: {
            fontFamily: 'Lato',
            fontWeight: LatoWeights.Bold,
            fontSize: 16,
            lineHeight: '1.3663em',
            textTransform: 'none',
        },
        caption: {
            fontWeight: LatoWeights.Bold,
            fontSize: '0.625rem',
            lineHeight: '2em',
            letterSpacing: '0.02em',
        },
        overline: {
            fontWeight: LatoWeights.Bold,
            fontSize: '0.6875rem',
            lineHeight: '1.4em',
            letterSpacing: '0.02em',
            textTransform: 'uppercase',
        },
    },
    buttonWidths: {
        small: '10.38rem',
        medium: '13.50rem',
        large: '20.25rem',
        xlarge: '31.06rem',
        full: '100%',
    },
    buttonHeights: {
        large: '4.625rem',
        regular: '3.125rem',
        compact: '2.938rem',
    },
    overrides: {
        MuiTabs: {
            indicator: {
                height: '2px',
                backgroundColor: '#2E3D63',
            },
        },
        MuiTab: {
            root: {
                '&$selected': {
                    color: '#2E3D63',
                },
            },
        },
    },
});

export default theme;
